import React, { memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { TextInput } from '@/components/common/text';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { useRadius } from '@/hooks/shared/useRadius';

interface NewsfeedSearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  onClear: () => void;
}

export const NewsfeedSearchBar: React.FC<NewsfeedSearchBarProps> = memo(({
  value,
  onChangeText,
  onClear,
}) => {
  const { colors } = useThemeColors();
  const radius = useRadius();
  const fieldHover = useHoverStyle('field');

  return (
    <View
      style={[styles.container, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }, fieldHover.viewStyle]}
      {...(fieldHover.mouseProps as any)}
    >
      <RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS width={18} height={18} color={colors.text.dimmer} />
      <TextInput
        style={[styles.input, { color: colors.text.primary }]}
        value={value}
        onChangeText={onChangeText}
        placeholder="Search posts..."
        placeholderTextColor={colors.text.dimmer}
        maxLength={CORK_BOARD_INPUT_LIMITS.searchMax}
        returnKeyType="search"
        autoCapitalize="none"
        autoCorrect={false}
        accessibilityLabel="Search community board posts"
        accessibilityRole="search"
      />
      {value.length > 0 && (
        <Hoverable
          hover="row"
          style={[styles.clearButton, { borderRadius: radius.sm }]}
          onPress={onClear}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
        >
          <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={14} height={14} color={colors.text.dimmer} />
        </Hoverable>
      )}
    </View>
  );
});

NewsfeedSearchBar.displayName = 'NewsfeedSearchBar';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingLeft: 17,
    paddingRight: 12,
    minHeight: 44,
    gap: 10,
  },
  input: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 0,
  },
  clearButton: {
    padding: 4,
  },
});
